import { ChevronsUpDown } from "lucide-react";
import { useHistory } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useIntercom } from "react-use-intercom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";

import { getName } from "components/navigation/menu-bar";
import { stringAvatar } from "components/shared/shared";
import {
  GetUserIfLoggedIn,
  User,
  getActiveOrganisation,
  switchOrganisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchUserMemberships } from "data/queries-common";
import * as Routes from "routes";

export function OrgSwitcher() {
  const { isMobile } = useSidebar();
  const [identityState, setIdentityState] = useIdentity();
  const activeOrg = getActiveOrganisation(identityState);
  const user: User | false = GetUserIfLoggedIn(identityState);
  const organisationList = user ? user.organisations : [];
  const history = useHistory();
  const { update } = useIntercom();
  const identityName = getName(identityState);

  const membershipsQuery = useQuery({
    queryKey: [identityState, QueryKeys.userMembership],
    queryFn: () => fetchUserMemberships(true),
  });

  const getOrgSubscription = (orgId: string) => {
    return membershipsQuery.data?.find((o) => o.organisationId === orgId)
      ?.subscription;
  };

  const avatarData = identityName ? stringAvatar(identityName) : null;
  const subscription = activeOrg
    ? getOrgSubscription(activeOrg.organisationId) ?? activeOrg.subscription
    : undefined;

  if (!identityName) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="pointer-events-none">
            <Skeleton className="size-8 rounded-lg" />
            <div className="grid flex-1 gap-1">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (organisationList.length <= 1) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-default">
            <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs">
              {avatarData?.children}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{identityName}</span>
              {subscription && (
                <span className="truncate text-xs">
                  {subscription}
                </span>
              )}
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs">
                {avatarData?.children}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{identityName}</span>
                {subscription && (
                  <span className="truncate text-xs">
                    {subscription}
                  </span>
                )}
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Organisations
            </DropdownMenuLabel>
            {organisationList
              .filter((o) => o !== activeOrg)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((org) => {
                const orgAvatarData = stringAvatar(org.name);
                const orgSub =
                  getOrgSubscription(org.organisationId) ?? org.subscription;
                const hasAccess = org.hasAccess !== false;

                return (
                  <DropdownMenuItem
                    key={org.organisationId}
                    onClick={async () => {
                      if (!hasAccess) return;
                      await switchOrganisation(
                        org,
                        identityState,
                        setIdentityState,
                        update,
                      ).finally(() => {
                        history.push(Routes.hub);
                      });
                    }}
                    className="gap-2 p-2"
                    disabled={!hasAccess}
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border text-[10px]">
                      {orgAvatarData.children}
                    </div>
                    <div className="grid flex-1 leading-tight">
                      <span className="truncate text-sm">{org.name}</span>
                      {orgSub && (
                        <span className="truncate text-xs">
                          {orgSub}
                        </span>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
